import '../Projects.css';
import './LifeCycleTabGraphTooltip.scss';
import { ResponsiveLine } from '@nivo/line';
import { createProjectStructure } from '../../shared/parsingDatas';
import { findParentNode } from '../../shared/utils';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import Spinner from '../../shared/Spinner';
import formatNumber from '../../services/mathematics/formatNumber';
import { useTranslation } from 'react-i18next';

const Coeff = require('./CoefAcv.js');

const LifeCycleTemporalGraph = ({
  onTab,
  equipments,
  title,
  currentIndicator,
  isDED,
  projectDdv,
  isRe2020,
  graphData,
  currentAllotment,
  projectArea,
  refUnit,
  parsedZones,
  isStacked,
  groups,
  currentProject,
  zones,
}) => {
  const [loading, setLoading] = useState(false);
  const [datas, setDatas] = useState({});
  const [stackedState, setStackedState] = useState(null);
  const [minY, setMinY] = useState(0);
  const [nbOfCharacters, setNbOfCharacters] = useState(20);
  const { t } = useTranslation();

  const { allGroups } = useSelector((state) => state.projects);

  const updateSize = () => {
    if (window.innerWidth < 1300) {
      setNbOfCharacters(6);
    } else if (window.innerWidth < 1500) {
      setNbOfCharacters(10);
    } else if (window.innerWidth < 1700) {
      setNbOfCharacters(14);
    } else {
      setNbOfCharacters(20);
    }
  };

  useEffect(() => {
    if (window.innerWidth < 1300) {
      setNbOfCharacters(6);
    }

    window.onresize = updateSize;
  }, []);

  useEffect(() => {
    if (equipments.length > 0) {
      setLoading(true);

      setStackedState(isStacked);
      createDataset();
    }
  }, [equipments, currentIndicator, isStacked, graphData]);

  const createDataset = () => {
    let initialDatas =
      graphData ||
      createProjectStructure(
        currentAllotment,
        currentProject.Sheets,
        zones,
        currentProject
      );

    if (!initialDatas) return;

    const buildingSiteZones = parsedZones
      .filter((pz) => pz.type === 'buildingSite')
      .map((pz) => pz.id);

    let datas = [];

    const nodeFamilies = initialDatas.children.map((gd) =>
      initialDatas.children[0].children.length === 0
        ? { label: gd.label, color: gd.color, ids: [gd.ParentId, gd.id] }
        : getFamilyFromParentNode(
            gd.id,
            currentAllotment.AllotmentNodes,
            gd.label,
            gd.color
          )
    );

    //Re2002 only
    if (isStacked) {
      datas = datas?.concat(
        nodeFamilies.map((nf) => {
          return {
            ids: nf.ids,
            id: nf.label,
            color: nf.color,
            data: [],
          };
        })
      );
    } else {
      datas = [
        {
          ids: nodeFamilies.reduce((p, c) => p.concat(c.ids), []),
          id: 'Impact',
          color: '#666',
          data: [],
        },
      ];
    }

    let processedDataSets = [];

    if (currentAllotment.id === 2) {
      // plot
      const plotFilter = (e, plotId) => plotId.includes(e.ProjectSheet.ZoneId);

      let plotData = datas.find((d) => d.id === 'Parcelle');
      const plotId = parsedZones
        .filter((pz) => pz.type === 'plot')
        .map((pz) => pz.id);
      let plotBuildingSite = parsedZones.find(
        (z) => plotId && z.ParentId === plotId[0] && z.type === 'buildingSite'
      );

      if (plotBuildingSite !== undefined) plotId.push(plotBuildingSite.id);

      if (plotData !== undefined) {
        plotData.data = getLCAbyNode(
          equipments.filter((e) => plotFilter(e, plotId)),
          currentIndicator.id,
          projectDdv,
          buildingSiteZones,
          projectArea,
          refUnit,
          allGroups
        );
        equipments = equipments.filter((e) => !plotFilter(e, plotId));
        processedDataSets.push(plotData.id);
      }

      //buildSites
      const buildingSiteFilter = (e, buildingSite) => {
        const zone = parsedZones.find((pz) => pz.id === e.ProjectSheet.ZoneId);

        return zone.name === buildingSite.id;
      };

      let buildingSites = datas.filter((d) => d.id.includes('Chantier'));

      for (let buildingSite of buildingSites) {
        buildingSite.data = getLCAbyNode(
          equipments.filter((e) => buildingSiteFilter(e, buildingSite)),
          currentIndicator.id,
          projectDdv,
          buildingSiteZones,
          projectArea,
          refUnit,
          allGroups
        );
        equipments = equipments.filter((e) => !buildingSiteFilter(e, buildingSite));
        processedDataSets.push(buildingSite.id);
      }

      // Water
      const waterFilter = (e) =>
        findParentNode(
          e.ProjectSheet.AllotmentNodes.find((el) => el.AllotmentId === 1),
          allGroups.flatAllotments,
          [],
          []
        )[0].name === 'Eau';

      let waterData = datas.find((d) => d.id === 'Eau');

      if (waterData !== undefined) {
        waterData.data = getLCAbyNode(
          equipments.filter((e) => waterFilter(e)),
          currentIndicator.id,
          projectDdv,
          buildingSiteZones,
          projectArea,
          refUnit,
          allGroups
        );
        equipments = equipments.filter((e) => !waterFilter(e));
        processedDataSets.push(waterData.id);
      }

      // Soil
      const soilFilter = (e) =>
        findParentNode(
          e.ProjectSheet.AllotmentNodes.find((el) => el.AllotmentId === 1),
          allGroups.flatAllotments,
          [],
          []
        )[0].name === 'Terre';

      let soilData = datas.find((d) => d.id === 'Terre');

      if (soilData !== undefined) {
        soilData.data = getLCAbyNode(
          equipments.filter((e) => soilFilter(e)),
          currentIndicator.id,
          projectDdv,
          buildingSiteZones,
          projectArea,
          refUnit,
          allGroups
        );
        equipments = equipments.filter((e) => !soilFilter(e));
        processedDataSets.push(soilData.id);
      }

      // Energy
      const energyFilter = (e) =>
        findParentNode(
          e.ProjectSheet.AllotmentNodes.find((el) => el.AllotmentId === 1),
          allGroups.flatAllotments,
          [],
          []
        )[0].name === 'Energie';

      let energyData = datas.find((d) => d.id === 'Energie');

      if (energyData !== undefined) {
        energyData.data = getLCAbyNode(
          equipments.filter((e) => energyFilter(e)),
          currentIndicator.id,
          projectDdv,
          buildingSiteZones,
          projectArea,
          refUnit,
          allGroups
        );
        equipments = equipments.filter((e) => !energyFilter(e));
        processedDataSets.push(energyData.id);
      }

      // Simple Energy
      // DATA_TRAD : fr
      const simpleEnergyFilter = (e) =>
        findParentNode(
          e.ProjectSheet.AllotmentNodes.find((el) => el.AllotmentId === 1),
          allGroups.flatAllotments,
          [],
          []
        )[0].name === 'Energie simplifiée';

      let simpleEnergyData = datas.find((d) => d.id === 'Energie simplifiée');

      if (simpleEnergyData !== undefined) {
        simpleEnergyData.data = getLCAbyNode(
          equipments.filter((e) => simpleEnergyFilter(e)),
          currentIndicator.id,
          projectDdv,
          buildingSiteZones,
          projectArea,
          refUnit,
          allGroups
        );
        equipments = equipments.filter((e) => !simpleEnergyFilter(e));
        processedDataSets.push(simpleEnergyData.id);
      }
    }

    for (let data of datas) {
      if (!processedDataSets.includes(data?.id)) {
        data.data = getLCAbyNode(
          equipments.filter((e) =>
            data.ids.includes(
              e.ProjectSheet.AllotmentNodes.find(
                (an) => an.AllotmentId === currentAllotment.id
              )?.id
            )
          ),
          currentIndicator.id,
          projectDdv,
          buildingSiteZones,
          projectArea,
          refUnit,
          allGroups
        );
      }
    }

    const newData = [...datas];

    newData.sort(
      (a, b) => Math.abs(a.data[projectDdv].y) - Math.abs(b.data[projectDdv].y)
    );

    let min = 0;

    for (let i = 0; i < newData[0].data.length && min >= 0; i++) {
      let tot = 0;

      for (let d of newData) tot += d.data[i].y;

      min = tot;
    }

    setMinY(min >= 0 ? 0 : 'auto');
    setDatas(newData);
    setLoading(false);
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  if (equipments.length === 0) {
    return (
      <div id="no-data-container">
        {t('APP.LIFECYCLE.LIFECYCLETEMPORALGRAPH.PH_E10D3A', {
          defaultValue: 'Pas de donnée',
        })}
      </div>
    );
  }

  return (
    <div
      id="stacked-vertical-bars-container"
      style={
        onTab
          ? { padding: '1em', height: '63.6vh', width: '95%' }
          : { height: 'auto' }
      }
    >
      {title ? <h4>{title}</h4> : null}
      {!loading && (
        <ResponsiveLine
          data={datas}
          margin={{ top: 60, right: 30, bottom: 140, left: 70 }}
          xScale={{
            type: 'linear',
          }}
          yScale={{
            type: 'linear',
            min: minY,
            max: 'auto',
            stacked: true,
            reverse: false,
          }}
          markers={[
            {
              axis: 'y',
              value: 0,
              lineStyle: { stroke: '#AAA', strokeWidth: 2 },
              legend: '',
              legendPosition: 'bottom-left',
            },
          ]}
          curve="linear"
          yFormat={(value) => formatNumber(value)}
          axisTop={null}
          sliceTooltip={({ slice }) => {
            let total = slice.points.reduce((p, c) => p + c.data.y, 0);

            return isStacked ? (
              <div className="graphTooltip">
                <div className="temporalTotal" style={{ 'padding-bottom': '8px' }}>
                  <strong style={{ color: '#40C5AF' }}>
                    {t('APP.LIFECYCLE.LIFECYCLETEMPORALGRAPH.PH_D0BE79', {
                      defaultValue: 'Total:',
                    })}{' '}
                    {formatNumber(total)}
                  </strong>
                  <strong style={{ color: 'black' }}>{' - '}</strong>
                  <span style={{ 'font-weight': 400, opacity: 0.7 }}>
                    {t('APP.LIFECYCLE.LIFECYCLETEMPORALGRAPH.PH_667F9B', {
                      defaultValue: 'Année:',
                    })}{' '}
                    {slice.points[0].data.x}
                  </span>
                </div>
                <div className="temporalList">
                  {slice.points.map((point, i, array) => {
                    return (
                      <div
                        key={point.id}
                        style={{
                          color: point.serieColor,
                          padding: '3px 0',
                        }}
                      >
                        <strong style={{}}>{point.serieId}</strong>
                        <strong style={{ color: 'black', opacity: 0.7 }}>
                          {' - '}
                          {point.data.yFormatted}
                        </strong>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="graphTooltip">
                <div className="temporalTotal">
                  <strong style={{ color: '#40C5AF' }}>
                    {t('APP.LIFECYCLE.LIFECYCLETEMPORALGRAPH.PH_FD924E', {
                      defaultValue: 'Total:',
                    })}{' '}
                    {formatNumber(total)}
                  </strong>
                  <strong style={{ color: 'black' }}>{' - '}</strong>
                  <span style={{ 'font-weight': 400, opacity: 0.7 }}>
                    {t('APP.LIFECYCLE.LIFECYCLETEMPORALGRAPH.PH_DF71D1', {
                      defaultValue: 'Année:',
                    })}{' '}
                    {slice.points[0].data.x}
                  </span>
                </div>
              </div>
            );
          }}
          axisRight={null}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Années',
            legendOffset: 36,
            legendPosition: 'end',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: `${currentIndicator.shortName} (${currentIndicator.unit}/m²)`,
            legendOffset: -60,
            legendPosition: 'end',
            format: (value) => formatNumber(value),
          }}
          colors={(d) => d.color}
          pointSize={0}
          pointBorderColor={{
            from: 'color',
            modifiers: [['darker', 0.3]],
          }}
          pointBorderWidth={2}
          pointLabelYOffset={-12}
          enableArea={stackedState}
          enableGridX={false}
          enableSlices="x"
          areaOpacity={0.7}
          useMesh={true}
        />
      )}
    </div>
  );
};

const getLCAbyNode = (
  projectSheets,
  indexId,
  ddv,
  buildingSites,
  projectArea,
  refUnit,
  allGroups
) => {
  let lcaTot = [];

  for (let i = 0; i <= ddv; i++) {
    lcaTot.push({ x: `${i}`, y: 0 });
  }

  // TODO promise array that for loop
  for (let ps of projectSheets) {
    let lcaSheet = 0;

    let fluidValues;
    if (ps.ProjectSheet.MasterDataEquipment.hasFluid) {
      const fluidACV =
        ps.ACV[`${indexId === 26 ? 1 : indexId}`][
          indexId === 1 ? 'dynamic' : 'static'
        ];
      fluidValues = {
        end: fluidACV.base.endfluide ? fluidACV.base.endfluide : 0,
        b1: fluidACV.usage.b1fluide ? fluidACV.usage.b1fluide : 0,
        b4: fluidACV.usage.b4fluide ? fluidACV.usage.b4fluide : 0,
        ddv: ps.ddv,
        ZoneId: ps.ProjectSheet.ZoneId,
      };
    }

    let panPhotoCoef = 1;

    if (ps.ProjectSheet.Caracs && ps.ProjectSheet.Caracs.length > 0) {
      const autoconso = ps.ProjectSheet.Caracs.find((c) =>
        c.name.includes('utoconsommation')
      );
      const production = ps.ProjectSheet.Caracs.find((c) =>
        c.name.includes('Production')
      );

      if (
        autoconso !== undefined &&
        production !== undefined &&
        autoconso.SheetCarac &&
        production.SheetCarac
      )
        panPhotoCoef =
          Number(autoconso.SheetCarac.value[0]) /
            Number(autoconso.SheetCarac.value[0]) || 1;
    }

    const lot = findParentNode(
      ps.ProjectSheet.AllotmentNodes.find(
        (allotmentNode) => allotmentNode.AllotmentId === 1
      ),
      allGroups.flatAllotments,
      [],
      []
    )[0].name;

    for (let lca of lcaTot) {
      const yearCoef = indexId === 1 ? Coeff[lca.x] : 1;

      lcaSheet +=
        getYearIc(
          ps,
          lca,
          ddv,
          buildingSites,
          indexId === 26 ? 1 : indexId,
          projectArea,
          lot
        ) *
        Number(ps.ProjectSheet.quantity) *
        yearCoef *
        panPhotoCoef;

      if (fluidValues) {
        lcaSheet += getFluidYearIc(
          fluidValues,
          lca,
          ddv,
          buildingSites,
          indexId === 26 ? 1 : indexId
        );
      }

      lca.y += lcaSheet / (refUnit === 'total' ? 1 : projectArea);
    }
  }

  return lcaTot;
};

const getYearIc = (ps, lca, ddv, buildingSites, indexId, projectArea, lot) => {
  const year = Number(lca.x);
  let values = ps.Indices.find((i) => i.id === indexId);

  if (values !== undefined) values = values.SheetIndex;

  // Filtering buildingSite equipments outside of year 0
  if (buildingSites.includes(ps.ProjectSheet.ZoneId)) {
    if (year === 0) {
      return values
        ? getTOTAL(values) - ((ps.ddv - 1) / ps.ddv) * getB1To4(values)
        : (ps.acvResults.base.edifChantier / ps.ProjectSheet.quantity) * projectArea;
    } else {
      return 0;
    }
  }

  if (!values) return 0;

  if (['Eau', 'Energie', 'Energie simplifiée'].includes(lot) && year !== 0)
    return getTOTAL(values);

  const isForfait = ps.type_fiche === 'FORFAIT';

  if (year === 0) {
    return getAProd(values) + getAEdif(values);
  } else if (year === ddv) {
    return getB1To4(values, isForfait) / ps.ddv + getC(values);
  } else {
    if (year % ps.ddv === 0) {
      if (ddv - year < ps.ddv)
        return (
          getB1To4(values, isForfait) / ps.ddv +
          (ddv / ps.ddv - Math.floor(ddv / ps.ddv)) *
            (getAEdif(values) + getAProd(values) + getC(values))
        );

      return (
        getB1To4(values, isForfait) / ps.ddv +
        getAEdif(values) +
        getAProd(values) +
        getC(values)
      );
    }

    return getB1To4(values, isForfait) / ps.ddv;
  }
};

const getFluidYearIc = (fv, lca, ddv, buildingSites, indexId) => {
  // Fluid impact is annually deducted from the Sheet LCA at the project lifespan scope
  // It's relying on the backend computation of the fluid impact
  const year = Number(lca.x);

  const usage = fv.b1 / ddv;
  const renew = fv.b4 / Math.floor(ddv / fv.ddv);
  const end = fv.end;

  if (buildingSites.includes(fv.ZoneId)) {
    if (year === 0) {
      return fv.b1 + fv.b4 + fv.end;
    }
    return 0;
  }

  switch (year) {
    case 0:
      return 0;
    case ddv:
      return usage + end;
    default:
      if (year % fv.ddv === 0) {
        return usage + renew;
      }

      return usage;
  }
};

const getB1To4 = (v, isForfait) => {
  if (isForfait) return v.B - v.B5 - v.B6 - v.B7;

  return v.B1 + v.B2 + v.B3 + v.B4;
};

const getAProd = (v) => {
  return v.AProd || v.A1 + v.A2 + v.A3;
};

const getAEdif = (v) => {
  return v.AEdif || v.A4 + v.A5;
};

const getB = (v) => {
  return v.B1 + v.B2 + v.B3 + v.B4 + v.B5 + v.B6 + v.B7 || v.B || v.BMaint + v.BOp;
};

const getC = (v) => {
  let c = v.C || v.C1 + v.C2 + v.C3 + v.C4;

  return c + v.D;
};

const getTOTAL = (v) => {
  return getAProd(v) + getAEdif(v) + getB(v) + getC(v) || v.TOTAL;
};

const getFamilyFromParentNode = (nodeId, nodes, label, color) => {
  let childs = nodes.filter((n) => n.ParentId === nodeId);
  let family = childs.length === 0 ? [nodeId] : [];

  for (let child of childs)
    family = family?.concat(getFamilyFromParentNode(child.id, nodes));

  return label && color ? { label: label, color: color, ids: family } : family;
};

export default LifeCycleTemporalGraph;
